import React from "react";
import { Breadcrumbs, Typography, Box, Link } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";

// Map every route path → { label, parent }
// parent = path of the logical parent (for breadcrumb trail)
const ROUTE_MAP = {
  "/dashboard": { label: "Dashboard" },
  "/organization": { label: "Organization" },
  "/department": { label: "Department" },
  "/clients": { label: "Clients" },
  "/clientprojects": { label: "Client Projects", parent: "/clients" },
  "/assets": { label: "Assets" },
  "/assetdetails": { label: "Asset Details", parent: "/assets" },
  "/employeedetails": { label: "Employees" },
  "/employeeprojects": {
    label: "Employee Projects",
    parent: "/employeedetails",
  },
  "/timesheet": { label: "Timesheet", parent: "/employeedetails" },
  "/timesheettracker": { label: "Timesheet Tasks", parent: "/employeedetails" },
  "/payslip": { label: "Payslip", parent: "/employeedetails" },
  "/leaverequest": { label: "Leave Request", parent: "/employeedetails" },
  "/leaveapproval": { label: "Leave Approval", parent: "/employeedetails" },
  "/employeesalaries": {
    label: "Employee Salaries",
    parent: "/employeedetails",
  },
  "/employeeassets": { label: "Employee Assets", parent: "/employeedetails" },
  "/employeeattendance": {
    label: "Employee Attendance",
    parent: "/employeedetails",
  },
  "/antivirus": { label: "Employee Antivirus", parent: "/employeedetails" },
  "/employee-allowance": {
    label: "Employee Allowance",
    parent: "/employeedetails",
  },
  "/experience-skills": {
    label: "Experience & Skills",
    parent: "/employeedetails",
  },
  "/users": { label: "Users" },
  "/roles": { label: "Roles", parent: "/users" },
  "/userroles": { label: "User Roles", parent: "/users" },
  "/screens": { label: "Screens", parent: "/users" },
  "/groups": { label: "Groups", parent: "/users" },
  "/permissions": { label: "Permissions", parent: "/users" },
  "/reports": { label: "Reports" },
  "/profile": { label: "Profile" },
};

// Build the full crumb trail for a given path
const buildCrumbs = (pathname) => {
  const key = pathname.toLowerCase();
  const crumbs = [];
  let current = ROUTE_MAP[key];
  let currentPath = key;

  if (!current) return [];

  // Walk up the parent chain
  while (current) {
    crumbs.unshift({ label: current.label, path: currentPath });
    currentPath = current.parent;
    current = currentPath ? ROUTE_MAP[currentPath] : null;
  }

  return crumbs;
};

const Breadcrumb = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const crumbs = buildCrumbs(location.pathname);

  if (crumbs.length === 0) return null;

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: 1,
        borderBottom: "1px solid #f0f0f0",
        backgroundColor: "#fff",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: "#bbb" }} />}
        aria-label="breadcrumb"
      >
        {/* Home always first */}
        <Link
          component="button"
          underline="hover"
          onClick={() => navigate("/dashboard")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.4,
            color: "#888",
            fontSize: 13,
            cursor: "pointer",
            "&:hover": { color: "#1976d2" },
            transition: "color 0.15s",
          }}
        >
          <HomeOutlinedIcon sx={{ fontSize: 15 }} />
          Home
        </Link>

        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return isLast ? (
            <Typography
              key={crumb.path}
              sx={{ fontSize: 13, fontWeight: 600, color: "#1976d2" }}
            >
              {crumb.label}
            </Typography>
          ) : (
            <Link
              key={crumb.path}
              component="button"
              underline="hover"
              onClick={() => navigate(crumb.path)}
              sx={{
                fontSize: 13,
                color: "#666",
                cursor: "pointer",
                "&:hover": { color: "#1976d2" },
                transition: "color 0.15s",
              }}
            >
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default Breadcrumb;
